/**
 * Word host helpers — Office.js WordApi (Word.run).
 * Reading, writing, formatting, comments, and agentic editing (applyOps).
 * WordApi 1.4+ required for comments; rest works on 1.1+.
 */

import { ParagraphLine, toParagraphLines } from "./sanitize";

/** Insert each line as its own paragraph, chaining after `anchor` (the
 *  selection when null). Word has no in-paragraph newline concept, so
 * multi-line text must become multiple paragraphs. */
function insertParagraphChain(
  context: Word.RequestContext,
  body: Word.Body,
  lines: ParagraphLine[],
  where: string
): void {
  let anchor: Word.Range | null = null;
  for (const ln of lines) {
    let para: Word.Paragraph;
    if (!anchor) {
      if (where === "start") para = body.insertParagraph(ln.text, Word.InsertLocation.start);
      else if (where === "end") para = body.insertParagraph(ln.text, Word.InsertLocation.end);
      else para = context.document.getSelection().insertParagraph(ln.text, Word.InsertLocation.after);
    } else {
      para = anchor.insertParagraph(ln.text, Word.InsertLocation.after);
    }
    anchor = para.getRange();
  }
}

export async function getSelectedText(): Promise<string> {
  return Word.run(async (context) => {
    const sel = context.document.getSelection();
    sel.load("text");
    await context.sync();
    return sel.text ?? "";
  });
}

export async function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();
    return body.text ?? "";
  });
}

/** Replace the current selection (or insert at cursor if nothing is selected).
 *  Multi-line text is written as consecutive paragraphs. */
export async function replaceSelection(text: string): Promise<void> {
  const lines = toParagraphLines(text);
  if (!lines.length) return;
  await Word.run(async (context) => {
    const sel = context.document.getSelection();
    sel.insertText(lines[0].text, Word.InsertLocation.replace);
    let anchor: Word.Range = sel;
    for (let i = 1; i < lines.length; i++) {
      const para = anchor.insertParagraph(lines[i].text, Word.InsertLocation.after);
      anchor = para.getRange();
    }
    await context.sync();
  });
}

/** Insert text right after the current selection / cursor, one paragraph
 *  per line. */
export async function insertAfterSelection(text: string): Promise<void> {
  const lines = toParagraphLines(text);
  if (!lines.length) return;
  await Word.run(async (context) => {
    insertParagraphChain(context, context.document.body, lines, "cursor");
    await context.sync();
  });
}

/** Append text to the end of the document body, one paragraph per line. */
export async function appendToDocument(text: string): Promise<void> {
  const lines = toParagraphLines(text);
  if (!lines.length) return;
  await Word.run(async (context) => {
    const body = context.document.body;
    for (const ln of lines) body.insertParagraph(ln.text, Word.InsertLocation.end);
    await context.sync();
  });
}

/** Add a review comment anchored to the current selection. Needs WordApi 1.4+. */
export async function addComment(commentText: string): Promise<void> {
  await Word.run(async (context) => {
    const sel = context.document.getSelection();
    sel.insertComment(commentText);
    await context.sync();
  });
}

// ---------------------------------------------------------------------------
// Search-based operations: let the LLM locate text by content (not by manual
// selection) and apply formatting / comments to every match. These bridge the
// gap that blocked "highlight all AI-related keywords in the whole document".
// ---------------------------------------------------------------------------

export interface SearchOptions {
  /** Treat query as a plain string (false) or regular expression (true). */
  useRegExp?: boolean;
  /** Case-sensitive match. */
  matchCase?: boolean;
  /** Match whole words only. */
  matchWholeWord?: boolean;
}

/** One located match — its paragraph index (1-based within body), the matched
 *  text, and a short context window for the LLM to disambiguate. */
export interface SearchHit {
  /** 1-based paragraph index within the document body. */
  para: number;
  /** The actual matched text. */
  match: string;
  /** Up to ~40 chars before + after the match, for disambiguation. */
  context: string;
}

/** Cap results to avoid dumping thousands of hits into the agent context. */
const MAX_SEARCH_HITS = 80;

function toSearchOptions(o: SearchOptions | undefined): Word.SearchOptions {
  // Built lazily so the module stays importable in non-Word hosts.
  return {
    matchCase: !!o?.matchCase,
    matchWholeWord: !!o?.matchWholeWord,
    // Office.js uses `matchWildcards` for regex; default false.
    matchWildcards: !!o?.useRegExp,
  } as unknown as Word.SearchOptions;
}

/** Search the whole document body and return match metadata (read-only).
 *  Lets the LLM answer "where are the AI-related sections?" without touching
 *  the document. */
export async function searchDocument(query: string, options?: SearchOptions): Promise<{ hits: SearchHit[]; total: number }> {
  if (!query) return { hits: [], total: 0 };
  return Word.run(async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    const hits: SearchHit[] = [];
    let total = 0;
    const so = toSearchOptions(options);

    for (let i = 0; i < paragraphs.items.length; i++) {
      const p = paragraphs.items[i];
      const found = p.search(query, so);
      found.load("items/text");
      await context.sync();
      if (!found.items.length) continue;

      // Load paragraph text once for context windows.
      p.load("text");
      await context.sync();
      const pText = p.text ?? "";
      total += found.items.length;
      for (const r of found.items) {
        if (hits.length >= MAX_SEARCH_HITS) break;
        const m = r.text ?? "";
        const idx = pText.indexOf(m);
        const ctxStart = Math.max(0, idx - 40);
        const ctxEnd = Math.min(pText.length, idx + m.length + 40);
        const ctx = (ctxStart > 0 ? "…" : "") + pText.slice(ctxStart, ctxEnd) + (ctxEnd < pText.length ? "…" : "");
        hits.push({ para: i + 1, match: m, context: ctx.replace(/\s+/g, " ").trim() });
      }
      if (hits.length >= MAX_SEARCH_HITS) break;
    }
    return { hits, total };
  });
}

/** Highlight every match of `query` with the given highlight color. Returns
 *  the number of matches actually formatted. Uses font.highlightColor. */
export async function highlightRanges(
  query: string,
  color: string,
  options?: SearchOptions & { bold?: boolean }
): Promise<number> {
  if (!query) return 0;
  return Word.run(async (context) => {
    const body = context.document.body;
    const found = body.search(query, toSearchOptions(options));
    found.load("items");
    await context.sync();
    let count = 0;
    for (const r of found.items) {
      r.load("font");
      r.font.highlightColor = color || "#FFFF00";
      if (options?.bold) r.font.bold = true;
      count++;
    }
    await context.sync();
    return count;
  });
}

/** Add a review comment to every match of `query`. Returns the number of
 *  comments inserted. Needs WordApi 1.4+. */
export async function addCommentToRanges(query: string, comment: string, options?: SearchOptions): Promise<number> {
  if (!query || !comment) return 0;
  if (!supportsComments()) return 0;
  return Word.run(async (context) => {
    const body = context.document.body;
    const found = body.search(query, toSearchOptions(options));
    found.load("items");
    await context.sync();
    let count = 0;
    for (const r of found.items) {
      r.insertComment(comment);
      count++;
    }
    await context.sync();
    return count;
  });
}

// ---------------------------------------------------------------------------
// Read back existing highlights — lets the LLM answer "what did I highlight
// earlier?" or "list every highlighted sentence". Word has no concept of page
// or line number (pages are a render artifact, not document structure), so we
// report paragraph index + in-paragraph offset + text + color instead.
// ---------------------------------------------------------------------------

export interface HighlightSpan {
  /** 1-based paragraph index within the document body. */
  para: number;
  /** Plain-text snippet of the highlighted run (a single word or a run of
   *  adjacent words sharing the same highlight color). */
  text: string;
  /** Hex color (e.g. "#FFFF00"). Empty string means the run has no highlight. */
  color: string;
  /** 1-based index of the first highlighted word within its paragraph. */
  startWord: number;
  /** Number of consecutive words merged into this span. */
  wordCount: number;
}

export interface HighlightReport {
  spans: HighlightSpan[];
  /** True count (spans may be capped to MAX_HIGHLIGHTS). */
  total: number;
}

const MAX_HIGHLIGHTS = 200;

function normalizeColor(c: string | undefined): string {
  // Office.js returns "#RRGGBB" or sometimes "" for no highlight. Normalise
  // empty/whitespace to "" so callers can treat it uniformly.
  return (c ?? "").trim();
}

/** Scan the document body paragraph-by-paragraph and collect every run of
 *  adjacent words sharing a non-empty highlight color. Pass `color` to filter
 *  to a single color (case-insensitive hex compare); omit it to return all. */
export async function getHighlights(colorFilter?: string): Promise<HighlightReport> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    const target = colorFilter ? normalizeColor(colorFilter).toLowerCase() : "";
    const spans: HighlightSpan[] = [];
    let total = 0;

    // Common delimiters for Chinese + English text — splits into "word-ish"
    // ranges whose font.highlightColor we can read. `split` is WordApi 1.3,
    // widely available (unlike Paragraph.words which is desktop-only).
    const DELIMS = [" ", "\t", ",", ".", "。", "，", "、", "；", ";", "：", ":", "？", "?", "！", "!", "（", "）", "(", ")", "「", "」", "“", "”", "\r", "\n"];

    for (let pi = 0; pi < paragraphs.items.length; pi++) {
      const para = paragraphs.items[pi];
      const ranges = para.split(DELIMS, true, true);
      ranges.load("items/text,items/font/highlightColor");
      await context.sync();

      let runStart = -1;       // index of first range in current run
      let runColor = "";       // color of current run
      let runText: string[] = [];

      const flushRun = () => {
        if (runStart >= 0 && runColor) {
          total += 1;
          if (spans.length < MAX_HIGHLIGHTS) {
            spans.push({
              para: pi + 1,
              text: runText.join(""),
              color: runColor,
              startWord: runStart + 1,
              wordCount: runText.length,
            });
          }
        }
        runStart = -1;
        runColor = "";
        runText = [];
      };

      for (let ri = 0; ri < ranges.items.length; ri++) {
        const rng = ranges.items[ri];
        const text = (rng.text ?? "").trim();
        if (!text) continue;     // skip empty shards left by trimSpacing
        const c = normalizeColor(rng.font.highlightColor).toLowerCase();
        const matches = target ? c === target : !!c;
        if (matches) {
          if (runStart >= 0 && c === runColor) {
            runText.push(text);
          } else {
            flushRun();
            runStart = ri;
            runColor = c;
            runText = [text];
          }
        } else {
          flushRun();
        }
      }
      flushRun();
    }
    return { spans, total };
  });
}

/** Clear (set to empty) highlight on every match of `query`, or on every word
 *  matching `color` when query is omitted. Handy for "remove all yellow marks". */
export async function clearHighlights(query?: string, color?: string): Promise<number> {
  return Word.run(async (context) => {
    const body = context.document.body;
    let cleared = 0;
    if (query) {
      const found = body.search(query, toSearchOptions(undefined));
      found.load("items");
      await context.sync();
      for (const r of found.items) {
        r.load("font");
        r.font.highlightColor = "";
        cleared++;
      }
    } else {
      // No query → clear by color across all paragraphs.
      const paragraphs = body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      const target = color ? normalizeColor(color).toLowerCase() : "";
      const DELIMS = [" ", "\t", ",", ".", "。", "，", "、", "；", ";", "：", ":", "？", "?", "！", "!", "（", "）", "(", ")", "「", "」", "“", "”", "\r", "\n"];
      for (const para of paragraphs.items) {
        const ranges = para.split(DELIMS, true, true);
        ranges.load("items/font/highlightColor");
        await context.sync();
        for (const rng of ranges.items) {
          const c = normalizeColor(rng.font.highlightColor).toLowerCase();
          if (c && (!target || c === target)) {
            rng.font.highlightColor = "";
            cleared++;
          }
        }
      }
    }
    await context.sync();
    return cleared;
  });
}

// ---------------------------------------------------------------------------
// Outline tree — present document structure to the LLM without dumping the
// whole body text. Uses paragraph.outlineLevel (WordApi 1.3+) which is the
// same property the navigation pane shows.
// ---------------------------------------------------------------------------

export interface OutlineNode {
  /** 1-based paragraph index within the document body. */
  para: number;
  /** Heading text (truncated to ~80 chars). */
  text: string;
  /** Outline level 1–9; 0 = body text (OutlineLevel.bodyText). */
  level: number;
}

/** Read every paragraph that is a heading (outlineLevel != bodyText) plus the
 *  first body paragraph after each heading, so the LLM can answer "what's the
 *  structure of this document?" cheaply. */
export async function getOutline(): Promise<{ nodes: OutlineNode[]; total: number }> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    const nodes: OutlineNode[] = [];
    for (let i = 0; i < paragraphs.items.length; i++) {
      const p = paragraphs.items[i];
      // outlineLevel must be loaded alongside text so the value is populated
      // after sync. Load in a batch per N paragraphs to keep request count sane.
      p.load(["outlineLevel", "text"]);
    }
    await context.sync();

    for (let i = 0; i < paragraphs.items.length; i++) {
      const p = paragraphs.items[i];
      // Word.OutlineLevel.bodyText = 0, OutlineLevel.outlineLevel1 = 1, etc.
      const lvl = (p.outlineLevel as unknown as number) || 0;
      if (lvl > 0) {
        const raw = (p.text ?? "").replace(/\s+/g, " ").trim();
        if (raw) {
          nodes.push({ para: i + 1, text: raw.length > 80 ? raw.slice(0, 80) + "…" : raw, level: lvl });
        }
      }
    }
    return { nodes, total: nodes.length };
  });
}

// ---------------------------------------------------------------------------
// Find & replace across the whole document. Word.js has no `replaceAll`, so we
// search → for each match call range.insertText(replacement, "replace") in a
// single Word.run batch (sync at the end). Returns the number replaced.
// ---------------------------------------------------------------------------

/** Replace every occurrence of `query` with `replacement` in the document body.
 *  Pass `options.useRegExp` to enable wildcard/regex semantics (Word wildcards,
 *  not full regex — see Office docs). */
export async function replaceAll(
  query: string,
  replacement: string,
  options?: SearchOptions
): Promise<number> {
  if (!query) return 0;
  return Word.run(async (context) => {
    const found = context.document.body.search(query, toSearchOptions(options));
    found.load("items");
    await context.sync();
    let count = 0;
    for (const r of found.items) {
      r.insertText(replacement, Word.InsertLocation.replace);
      count++;
    }
    await context.sync();
    return count;
  });
}

export function supportsComments(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.4");
  } catch {
    return false;
  }
}

/** Best-effort document author (from built-in document properties). */
export async function getAuthor(): Promise<string> {
  try {
    return await Word.run(async (context) => {
      const props = context.document.properties;
      props.load("author");
      await context.sync();
      return props.author || "";
    });
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// Agentic editing: apply a list of operations the model produced.
// All Word.* enum reads happen INSIDE functions (lazy) so this module stays
// importable in Excel/PowerPoint hosts where the Word global is absent.
// ---------------------------------------------------------------------------

import { DocOp } from "../core/types";

function builtInStyle(style: unknown): Word.BuiltInStyleName | null {
  const map: Record<string, Word.BuiltInStyleName> = {
    title: Word.BuiltInStyleName.title,
    subtitle: Word.BuiltInStyleName.subtitle,
    heading1: Word.BuiltInStyleName.heading1,
    heading2: Word.BuiltInStyleName.heading2,
    heading3: Word.BuiltInStyleName.heading3,
    heading4: Word.BuiltInStyleName.heading4,
    normal: Word.BuiltInStyleName.normal,
    quote: Word.BuiltInStyleName.quote,
  };
  const k = String(style ?? "").toLowerCase().replace(/\s+/g, "");
  return map[k] ?? null;
}

function alignOf(a: unknown): Word.Alignment | null {
  const map: Record<string, Word.Alignment> = {
    left: Word.Alignment.left,
    center: Word.Alignment.centered,
    centre: Word.Alignment.centered,
    centered: Word.Alignment.centered,
    right: Word.Alignment.right,
    justify: Word.Alignment.justified,
    justified: Word.Alignment.justified,
  };
  return map[String(a ?? "").toLowerCase()] ?? null;
}

function applyFont(font: Word.Font, op: DocOp) {
  if (typeof op.bold === "boolean") font.bold = op.bold;
  if (typeof op.italic === "boolean") font.italic = op.italic;
  if (typeof op.underline === "boolean") font.underline = op.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
  if (typeof op.fontSize === "number") font.size = op.fontSize;
  if (typeof op.color === "string") font.color = op.color;
}

function formatParagraph(para: Word.Paragraph, op: DocOp) {
  const st = builtInStyle(op.style);
  if (st) para.styleBuiltIn = st;
  const al = alignOf(op.alignment);
  if (al) para.alignment = al;
  applyFont(para.font, op);
}

function trunc(s: string): string {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > 24 ? t.slice(0, 24) + "…" : t;
}

/** Execute a list of editing operations against the active Word document. */
export async function applyOps(ops: DocOp[]): Promise<string[]> {
  const log: string[] = [];
  await Word.run(async (context) => {
    const body = context.document.body;
    for (const op of ops) {
      const kind = String(op.op || "");
      const text = String(op.text ?? "");
      const where = String(op.where || "cursor");
      // Effective style: keep the op's explicit style, else a Markdown heading
      // hinted by the sanitized line ("## 标题" → heading2).
      const styleOf = (ln: ParagraphLine) => (op.style ? String(op.style) : ln.style);

      if (kind === "insertParagraph") {
        const lines = toParagraphLines(text);
        if (!lines.length) {
          log.push("插入段落：内容为空，已跳过");
          continue;
        }
        let anchor: Word.Range | null = null;
        for (const ln of lines) {
          let para: Word.Paragraph;
          if (!anchor) {
            if (where === "start") para = body.insertParagraph(ln.text, Word.InsertLocation.start);
            else if (where === "end") para = body.insertParagraph(ln.text, Word.InsertLocation.end);
            else para = context.document.getSelection().insertParagraph(ln.text, Word.InsertLocation.after);
          } else {
            para = anchor.insertParagraph(ln.text, Word.InsertLocation.after);
          }
          formatParagraph(para, { ...op, style: styleOf(ln) });
          anchor = para.getRange();
        }
        log.push(`插入${op.style || lines[0].style ? String(op.style || lines[0].style) + " " : ""}段落${lines.length > 1 ? `（拆为 ${lines.length} 段）` : ""}：${trunc(text)}`);
        await context.sync();
      } else if (kind === "replaceSelection") {
        const lines = toParagraphLines(text);
        if (!lines.length) {
          log.push("替换选区：内容为空，已跳过");
          continue;
        }
        const range = context.document.getSelection();
        range.insertText(lines[0].text, Word.InsertLocation.replace);
        const st = builtInStyle(op.style);
        if (st) range.styleBuiltIn = st;
        applyFont(range.font, op);
        const al = alignOf(op.alignment);
        if (al) {
          const paras = range.paragraphs;
          paras.load("items");
          await context.sync();
          paras.items.forEach((p) => (p.alignment = al));
        }
        let anchor: Word.Range = range;
        for (let i = 1; i < lines.length; i++) {
          const para = anchor.insertParagraph(lines[i].text, Word.InsertLocation.after);
          formatParagraph(para, { ...op, style: styleOf(lines[i]) });
          anchor = para.getRange();
        }
        log.push(lines.length > 1 ? `替换选区内容（拆为 ${lines.length} 段）` : "替换选区内容");
        await context.sync();
      } else if (kind === "formatSelection") {
        const range = context.document.getSelection();
        const st = builtInStyle(op.style);
        if (st) range.styleBuiltIn = st;
        applyFont(range.font, op);
        const al = alignOf(op.alignment);
        if (al) {
          const paras = range.paragraphs;
          paras.load("items");
          await context.sync();
          paras.items.forEach((p) => (p.alignment = al));
        }
        log.push("设置选区格式");
        await context.sync();
      } else if (kind === "insertText") {
        const lines = toParagraphLines(text);
        if (!lines.length) {
          log.push("插入文本：内容为空，已跳过");
          continue;
        }
        if (lines.length === 1) {
          if (where === "start") body.insertText(lines[0].text, Word.InsertLocation.start);
          else if (where === "end") body.insertText(lines[0].text, Word.InsertLocation.end);
          else context.document.getSelection().insertText(lines[0].text, Word.InsertLocation.after);
          log.push("插入文本");
        } else {
          // Multi-line content → paragraphs (Word has no in-paragraph newline).
          insertParagraphChain(context, body, lines, where);
          log.push(`插入文本（拆为 ${lines.length} 段）`);
        }
        await context.sync();
      } else if (kind === "insertTable") {
        const rows = (op.rows as unknown[][]) || [];
        if (Array.isArray(rows) && rows.length) {
          const cols = Math.max(...rows.map((r) => (Array.isArray(r) ? r.length : 0)));
          const data = rows.map((r) => {
            const a = Array.isArray(r) ? r.map((x) => String(x ?? "")) : [];
            while (a.length < cols) a.push("");
            return a;
          });
          const loc = where === "start" ? Word.InsertLocation.start : Word.InsertLocation.end;
          body.insertTable(data.length, cols, loc, data);
          log.push(`插入表格 ${data.length}×${cols}`);
          await context.sync();
        }
      } else if (kind === "highlightSearch") {
        // Search-driven highlight — lets the LLM mark every occurrence of one
        // or more keywords (e.g. "highlight all AI-related terms") without any
        // manual selection. op.query is a single string OR an array of strings.
        const queries = Array.isArray(op.query) ? (op.query as string[]).map((s) => String(s ?? "")) : [String(op.query ?? "")];
        const color = String(op.color || "#FFFF00");
        const useRegExp = !!op.useRegExp;
        const matchCase = !!op.matchCase;
        const bold = !!op.bold;
        let totalMarked = 0;
        for (const q of queries) {
          if (!q) continue;
          const found = body.search(q, { matchWildcards: useRegExp, matchCase, matchWholeWord: false });
          found.load("items");
          await context.sync();
          for (const r of found.items) {
            r.load("font");
            r.font.highlightColor = color;
            if (bold) r.font.bold = true;
            totalMarked++;
          }
          await context.sync();
        }
        log.push(`高亮 ${queries.length} 个关键词，共 ${totalMarked} 处`);
      } else if (kind === "commentSearch") {
        // Search-driven comments — attach a review comment to every match.
        // Requires WordApi 1.4+; silently skipped on older hosts.
        if (supportsComments()) {
          const queries = Array.isArray(op.query) ? (op.query as string[]) : [String(op.query ?? "")];
          const commentText = String(op.comment ?? "");
          let totalMarked = 0;
          for (const q of queries) {
            if (!q || !commentText) continue;
            const found = body.search(q, { matchWildcards: !!op.useRegExp, matchCase: !!op.matchCase, matchWholeWord: false });
            found.load("items");
            await context.sync();
            for (const r of found.items) {
              r.insertComment(commentText);
              totalMarked++;
            }
            await context.sync();
          }
          log.push(`批注 ${queries.length} 个关键词，共 ${totalMarked} 处`);
        } else {
          log.push("当前 Word 版本不支持批注（需 WordApi 1.4+），已跳过 commentSearch");
        }
      } else {
        log.push(`忽略未知操作：${kind}`);
      }
    }
  });
  return log;
}
